'use client';

import { useTranslation } from '@/features/i18n';
import { VideoProgressBar } from '@/features/resume-watching';
import { VideoBackdrop } from '@/features/video-backdrop';
import { AddToWatchListButton } from '@/features/watchlist';
import { Link } from '@/ui/Link';
import { VideoButton } from '@/ui/buttons/VideoButton';
import { Artwork } from '@/ui/images/Artwork';
import { ArtworkLogo } from '@/ui/images/ArtworkLogo';
import { AgeRating } from '@/ui/metadata/AgeRating';
import AnimatedPreview from '@/ui/preview/AnimatedPreview';
import { USE_IMAGE_BASED_PREVIEW } from '@/util/constants';
import { PlayCircleIcon } from '@heroicons/react/24/solid';
import {
  AssetType,
  CuratedAssetType,
} from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import { formatSecondsToTimeString, mapToRelativeTime } from '@vimond/utils';
import { getEnvVar } from '@vimond/utils/env';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';

type Props = {
  asset: CuratedAssetType | AssetType;
};

// reduce CDN cost by delaying video preview
const PREVIEW_DELAY = 500; // 0.5 second

export const VideoCard = ({
  asset: assetProp,
  className,
  ...props
}: Props & React.ComponentPropsWithoutRef<'article'>) => {
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const showPreviewTimer = useRef<NodeJS.Timeout | null>(null);

  const router = useRouter();
  const { i18n, t } = useTranslation();
  const { language } = i18n;

  // check if asset is of type CuratedAssetType or AssetType
  const asset =
    (assetProp as CuratedAssetType).content !== undefined
      ? (assetProp as CuratedAssetType).content!
      : (assetProp as AssetType);

  if (!asset) return <></>;

  const handleMouseEnter = () => {
    if (showPreviewTimer.current) {
      clearTimeout(showPreviewTimer.current);
      showPreviewTimer.current = null;
    }
    showPreviewTimer.current = setTimeout(() => {
      setShowPreview(true);
    }, PREVIEW_DELAY);
  };
  const handleMouseLeave = () => {
    if (showPreviewTimer.current) {
      clearTimeout(showPreviewTimer.current);
      showPreviewTimer.current = null;
    }
    setShowPreview(false);
  };

  const hasVideoPreview = asset.markers?.some(
    (marker) => marker.type === getEnvVar('NEXT_PUBLIC_PREVIEW_MARKER_TYPE'),
  );

  const isVerticalAsset = asset.aspect16x9 === false;

  return (
    <div
      onClick={() =>
        router.push(
          `/${!(getEnvVar('NEXT_PUBLIC_ALTERNATIVE_WATCH_LAYOUT') === 'true') ? 'video' : 'watch'}/${asset.id}`,
        )
      }
      className="cursor-pointer"
    >
      <article
        {...props}
        className={cn('group relative', className && className)}
      >
        <div className="relative border-background-secondary mx-1 box-content border flex h-0 pb-[56%] cursor-pointer flex-col overflow-hidden rounded-lg">
          {asset.images?.defaultUrl && (
            <Artwork
              alt=""
              src={asset.images?.defaultUrl}
              location="portal-carousel"
              fallbackToDefault
              fill
              sizes="25vw"
              className="object-cover"
            />
          )}
          {asset.duration > 0 && (
            <div className="bg-black bg-opacity-55 rounded-sm absolute bottom-2 right-2 z-50 px-2">
              <span className="text-xs lg:text-sm font-bold text-white">
                {formatSecondsToTimeString(asset.duration)}
              </span>
            </div>
          )}
          <VideoProgressBar asset={asset} />
        </div>

        <div className="mx-1 mt-2 text-left text-primary lg:mt-4">
          <h1 className="text-sm lg:text-base font-bold">{asset.title}</h1>
          {getEnvVar('NEXT_PUBLIC_SHOW_PUBLISH_DATE') === 'true' &&
            asset.publishing.start && (
              <div className="mt-1">
                <span className="text-xs lg:text-sm">
                  {mapToRelativeTime(asset.publishing.start, t, language)}
                </span>
              </div>
            )}
        </div>

        <div className="absolute left-0 right-0 top-0 z-[100] hidden aspect-video lg:block">
          <div
            className="border-background-secondary bg-background-secondary absolute bottom-0 left-0 right-0 top-0 border opacity-0  transition-all duration-300 ease-out group-hover:scale-110 group-hover:opacity-100 group-hover:delay-700"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="absolute hidden h-full w-full flex-col group-hover:flex">
              {asset.images?.defaultUrl && (
                <Artwork
                  alt=""
                  src={asset.images.defaultUrl}
                  location="portal-carousel"
                  fallbackToDefault
                  fill
                  sizes="25vw"
                  className="object-cover"
                />
              )}
              {showPreview &&
                (USE_IMAGE_BASED_PREVIEW ? (
                  <AnimatedPreview asset={asset} isVertical={isVerticalAsset} />
                ) : (
                  hasVideoPreview && <VideoBackdrop asset={asset} />
                ))}
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              {asset.images?.defaultUrl && (
                <div className="max-w-[30%]">
                  <ArtworkLogo src={asset.images?.defaultUrl} />
                </div>
              )}
              <div className="flex items-center gap-1 ">
                <Link href={`/watch/${asset.id}`}>
                  <PlayCircleIcon className="h-11 w-11" />
                </Link>
                <AddToWatchListButton asset={asset} />
                {!(
                  getEnvVar('NEXT_PUBLIC_ALTERNATIVE_WATCH_LAYOUT') === 'true'
                ) && <VideoButton assetId={asset.id} />}
              </div>
              <div className="mt-1 flex items-center gap-4 text-xs opacity-70">
                {asset['age-rating'] && (
                  <AgeRating ageRating={asset['age-rating']} />
                )}
                <div>
                  {asset.season && <span>S{asset.season}</span>}
                  {asset.episode && <span>E{asset.episode}</span>}
                </div>
              </div>
              <div className="mt-1 w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                {asset.labels?.split(',').map((label) => (
                  <span key={label} className="mr-1 uppercase opacity-50">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
